<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <!-- Header -->
    <TopNavbar />
    <CentralHeader
      :title="'Activations'"
      :count="isLoading ? null : filteredActivations.length"
      @search="filterActivations"
      :route-name="'CreateActivationPage'"
      :hasFilter="true"
      :showFilter="showFilter"
      :toggleFilter="toggleFilter"
      :filterApplied="hasActiveFilters"
    />

    <!-- Filter Overlay -->
    <FilterOverlay
      v-if="showFilter"
      :title="'Activation Filters'"
      @cancel="toggleFilter"
      :filters="dynamicFilters"
      @filter-change="handleFilterChange"
      @apply-filters="handleApplyFilters"
      @clear="clearAllFilters"
    />

    <!-- Main Content -->
    <div class="flex-1 flex flex-col mb-14 overflow-y-auto">
      <div
        v-if="isLoading"
        class="text-center flex-1 flex justify-center items-center m-auto h-full"
      >
        Loading activations...
      </div>
      <div
        v-else-if="errorMessage"
        class="text-center flex-1 flex justify-center items-center m-auto h-full text-red-500"
      >
        {{ errorMessage }}
      </div>
      <div
        v-else-if="Array.isArray(filteredActivations) && filteredActivations.length === 0"
        class="text-center flex-1 flex justify-center gap-3 items-center m-auto h-full text-total-white-500"
      >
        <div v-if="isSearchMode">No activations available.</div>
        <div v-else>No Projects</div>
        <!--<button
          v-else
          @click="goToCreateActivation"
          class="text-prime-golden flex items-center gap-3"
        >
          <i class="fa fa-plus text-2xl mr-2"></i>
          Click to add activations
        </button>-->
      </div>
      <div v-else>
        <ActivationCard
          v-for="activation in activations"
          :key="activation.id"
          :activation="activation"
        />
      </div>
      <!-- Pagination Controls (Fixed at the bottom) -->
      <div
        class="flex justify-center items-center py-4 bg-pitch-black-900 fixed bottom-0 w-full border-t border-gray-600"
      >
        <button
          @click="previousPage"
          :disabled="currentPage == 1"
          class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
        >
          <i class="fa fa-chevron-left"></i>
        </button>

        <span class="text-sm text-total-white mx-4">
          {{ currentPage }}/{{ totalPages }}
        </span>

        <button
          @click="nextPage"
          :disabled="currentPage >= totalPages"
          class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
        >
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Pagination Controls (Fixed at the bottom) -->
    <div
      class="flex justify-center items-center py-4 bg-pitch-black-900 fixed bottom-0 w-full border-t border-gray-600"
    >
      <button
        @click="previousPage"
        :disabled="currentPage == 1"
        class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-left"></i>
      </button>

      <span class="text-sm text-total-white mx-4">
        {{ currentPage }}/{{ totalPages }}
      </span>

      <button
        @click="nextPage"
        :disabled="currentPage >= totalPages"
        class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import TopNavbar from "@/components/TopNavbar.vue";
import CentralHeader from "@/components/CentralHeader.vue";
import ActivationCard from "@/components/ActivationCard.vue";
import FilterOverlay from "@/components/FilterOverlay.vue";
import { useActivationStore } from "@/stores/activation";

const activationStore = useActivationStore();

const isLoading = computed(() => activationStore.isLoading);
const activations = computed(() => activationStore.activations);
const currentPage = ref(1);
const totalActivations = computed(() => activationStore.totalActivations);
const pageSize = computed(() => activationStore.pageSize);
const totalPages = computed(
  () =>
    (totalPages.value = Math.max(1, Math.ceil(totalActivations.value / pageSize.value)))
);

const errorMessage = ref("");
const filteredActivations = ref([]);
const showFilter = ref(false);
const isSearchMode = ref(false);

// Active filters state
const activeFilters = ref({
  projectType: [],
  projects: [],
});
// Define filters dynamically
const dynamicFilters = [
  {
    key: "status",
    label: "Status",
    type: "select",
    options: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
    selected: [],
    dropdownOpen: false,
  },
  {
    key: "account",
    label: "Account",
    type: "multi-select",
    options: [
      { id: 1, label: "Account 1" },
      { id: 2, label: "Account 2" },
      { id: 3, label: "Account 3" },
    ],
    selected: [],
    dropdownOpen: false,
    searchQuery: "",
  },
  {
    key: "agent",
    label: "Agent",
    type: "multi-select",
    options: [
      { id: 1, label: "Agent A" },
      { id: 2, label: "Agent B" },
      { id: 3, label: "Agent C" },
    ],
    selected: [],
    dropdownOpen: false,
    searchQuery: "",
  },
];
// Handle Apply Filters
const handleApplyFilters = (appliedFilters) => {
  console.log("Applied Filters:", appliedFilters);
  showFilter.value = false;
};
const toggleFilter = () => {
  showFilter.value = !showFilter.value;
};

// Handle filter changes
const handleFilterChange = (filterKey, selectedValues) => {
  activeFilters.value[filterKey] = selectedValues;
  console.log("Active Filters:", activeFilters.value);
};

// Check if there are active filters
const hasActiveFilters = computed(() => {
  return Object.values(activeFilters.value).some((filter) => filter.length > 0);
});

// Clear all filters
const clearAllFilters = () => {
  activeFilters.value = {
    projectType: [],
    projects: [],
  };
  console.log("All filters cleared");
};

const fetchActivations = async () => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    await activationStore.fetchActivations(currentPage.value, activationStore.pageSize);
    activations.value = activationStore.activations;
    currentPage.value = activationStore.currentPage;
    totalPages.value = Math.ceil(activationStore.totalAgents / activationStore.pageSize);
    filteredActivations.value = activations.value;
  } catch (error) {
    errorMessage.value = "Failed to load activations.";
    console.error("Error:", error);
  } finally {
    isLoading.value = false;
  }
};

// Filter activations based on search query
const filterActivations = async (query) => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    isSearchMode.value = !!query; // Enable search mode if query exists
    currentPage.value = 1; // Reset to the first page when searching
    if (!query) {
      await fetchActivations();
      filteredActivations.value = activations.value; // Show all activations if query is empty
    } else {
      // Call the search endpoint from the activation store
      await activationStore.searchActivations(query, currentPage.value);
      filteredActivations.value = activationStore.activations; // Update filtered activations
    }
    totalPages.value = Math.max(
      1,
      Math.ceil(filteredActivations.value.length / activationStore.size)
    );
  } catch (error) {
    errorMessage.value = "Failed to filter activations.";
    console.error("Error filtering activations:", error);
  } finally {
    isLoading.value = false;
  }
};

const previousPage = async () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
  }
  await fetchActivations();
};

const nextPage = async () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
    console.log(currentPage.value, totalPages.value);
  }
  await fetchActivations();
};

onMounted(() => {
  fetchActivations();
});
</script>

<style scoped>
/* Add specific styles if necessary */
</style>
